/*
 * Copyright (C) GMS Inc - All Rights Reserved
 * This file is part of GMSCloud. - Proprietary and confidential
 * Unauthorized copying of this file, via any medium is strictly prohibited
 *
 * Created on   : 01-Aug-2026
 * @author      : Vijayakumar S, with Aravindan (Claude-assisted)
 * @since       : 1.5.0.0
 * Requirement# : Communication Management §9 — screen 4, Review Detail
 * Purpose      : Proposed content against the current active version, with a real
 *                word-level diff and a token delta; approve, or reject with comments.
 * -----------------------------------------------------------------------------
 * Revision History
 * -----------------------------------------------------------------------------
 * Requirement/
 * Issue/WorkItem |   DATE      |   AUTHOR       |   DESCRIPTION OF CHANGE
 * -----------------------------------------------------------------------------
 * §9             | 01-Aug-2026 | Vijayakumar S  | Initial creation
 * Comms review   | 06-Aug-2026 | Claude         | Three simplifications, at the user's request:
 * simplify                                        (1) the token-delta panel ("Token changes" /
 *                                                 "Tokens in the proposed body") now renders
 *                                                 only when there is something to report —
 *                                                 real additions/removals, an unknown token, or
 *                                                 the T10 "cannot be computed" disclaimer — not
 *                                                 on every routine review; the "No tokens added
 *                                                 or removed" no-op text is gone, and so is the
 *                                                 Paper around it when that was all it had to
 *                                                 say. (2) the Subject field-comparison row is
 *                                                 now Email-only (`isEmail`, same gate
 *                                                 TemplateEditor already uses) — SMS templates
 *                                                 structurally never carry a subject. (3) the
 *                                                 Version field-comparison row is removed
 *                                                 entirely — redundant with the `v{n}` chip in
 *                                                 the header, and its "current" side was already
 *                                                 "Not available" for nearly every real revision
 *                                                 (T10). The unknown-token error and the T10
 *                                                 disclaimer are both untouched — see the
 *                                                 "WHAT WAS BUILT" note above for why those stay.
 * Comms plain    | 06-Aug-2026 | Claude         | A v1 submission (`isPlainNewTemplate`) now
 * new-template                                     renders Name/Subject/Body as plain values —
 *                                                 no "Current active version | Proposed" columns,
 *                                                 no "N words added" summary, no "New template —
 *                                                 nothing to compare against" disclaimer (removed,
 *                                                 not just reworded: nothing is presented as a
 *                                                 comparison any more, so there is nothing left to
 *                                                 disclaim) — at the user's request, to match how
 *                                                 the authoring screen shows the same fields. A
 *                                                 revision (real diff, or T10's liveUnavailable)
 *                                                 is entirely untouched by this change.
 * Comms notes    | 06-Aug-2026 | Claude         | Two changes to the Decision section, at the
 * token insert                                    user's request: (1) dropped the footer caption
 *                                                 ("There is no edit-then-approve path...") —
 *                                                 the buttons' own labels already say as much.
 *                                                 (2) gave Review comments a way to insert a real
 *                                                 token instead of typing `{{...}}` from memory.
 *                                                 FIRST tried as a full `TokenPicker` panel beside
 *                                                 the Decision Paper (a two-column Grid, mirroring
 *                                                 TemplateEditor's own layout) — SUPERSEDED later
 *                                                 the same day by a compact "Insert a token"
 *                                                 dropdown in the Decision header instead, at the
 *                                                 user's follow-up request: a permanent, spread-out
 *                                                 chip panel was too much standing screen space for
 *                                                 an occasional insert here, unlike TemplateEditor's
 *                                                 authoring screen where that is the point. See the
 *                                                 dropdown's own inline comment for the final shape;
 *                                                 `insertNoteToken`'s caret-insertion logic (still
 *                                                 identical to TemplateEditor's `insertToken`) did
 *                                                 not need to change — only what triggers it did.
 * Comms header   | 06-Aug-2026 | Claude         | Several changes to the top of this screen, at
 * cleanup + cancel                                 the user's request, in two passes the same
 *                                                 day. FIRST: (1) removed the channel/request-
 *                                                 type/version chip row under the template name
 *                                                 entirely (every mention of a "header chip"
 *                                                 elsewhere in this file's comments predates this
 *                                                 and is now stale — the version is genuinely not
 *                                                 shown anywhere on this screen any more). (2)
 *                                                 added a "Cancel" button beside Approve/Request
 *                                                 changes/Reject, navigating to
 *                                                 `/communications/templates` (defaults to the
 *                                                 Email tab) — the same target TemplateEditor's
 *                                                 own Cancel button uses. SECOND, a follow-up
 *                                                 message: (3) removed the entire header `Stack`
 *                                                 that was left — the `proposed.name` heading
 *                                                 (redundant with the read-only "Template name"
 *                                                 field already shown below it) and the "Back to
 *                                                 queue" button (redundant with the new Cancel
 *                                                 button, and Cancel is the one that stayed —
 *                                                 "Back to queue" targeted `/communications/review`,
 *                                                 the OLD standalone queue page, not this screen's
 *                                                 actual list). The outcome/error states' own
 *                                                 separate "Back to the review queue" buttons are
 *                                                 UNCHANGED — this only touched the main render's
 *                                                 header, the only place the redundancy existed.
 * Comms reject   | 06-Aug-2026 | Claude         | Fixes the actual bug behind "Reject is not
 * not-under-                                       working correctly": every row on the Email/SMS
 * review fix                                       tabs opens this screen on click regardless of
 *                                                 status (DRAFT/APPROVED/PUBLISHED/
 *                                                 CHANGES_REQUESTED, not just UNDER_REVIEW — see
 *                                                 `findManageCatalog`), but
 *                                                 `MessageTemplateMasterService` enforces
 *                                                 UNDER_REVIEW-only for approve/reject/
 *                                                 requestChanges. Clicking any decision button on
 *                                                 anything else always 400'd; the UI never checked
 *                                                 this itself, so the button looked broken instead
 *                                                 of simply not being offered. New `isUnderReview`
 *                                                 gates the token dropdown, the notes field, and
 *                                                 all three decision buttons behind an informative
 *                                                 "nothing to decide" notice instead, and also
 *                                                 gates the blast-radius warning above (which
 *                                                 described the same unavailable action). Cancel
 *                                                 stays either way.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import Grid from '@mui/material/Grid';
import InputLabel from '@mui/material/InputLabel';
import ListSubheader from '@mui/material/ListSubheader';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import HtmlPreview from './HtmlPreview';
import { useOidcAccessToken } from '@axa-fr/react-oidc';

import {
  approveTemplate,
  fetchManageCatalog,
  publishTemplate,
  rejectTemplate,
  requestTemplateChanges,
} from './MessageTemplateService';
import { placeholderFor, tokensByGroup } from './CommunicationTokenCatalog';
import {
  CHANNEL_EMAIL,
  TemplateStatus,
  TemplateSummary,
  requestTypeOf,
  statusLabel,
} from './CommunicationTypes';
import { DiffRun, diffTemplates, summariseDiff } from './templateDiff';
import { TemplateVersionHistory } from './TemplateVersionHistory';
import { TemplateReviewThread } from './TemplateReviewThread';

/**
 * =============================== THE DIFF DECISION ===============================
 *
 * §9 asks for "the proposed content shown against the current active version, side by
 * side". Two panes of raw text would satisfy that sentence and fail its purpose: a
 * provider approving here makes the change live for EVERY tenant simultaneously, with no
 * per-tenant fork and no staged rollout, so the screen's real job is to make the actual
 * difference impossible to miss.
 *
 * WHAT WAS BUILT — a real diff, in three layers, most consequential first:
 *
 *   1. TOKEN DELTA (top, most prominent). Tokens added, tokens removed, and any token in
 *      the proposed body that is NOT in the C4 catalog. This is deliberately ABOVE the
 *      prose diff because it is the highest-consequence and least visible class of change:
 *      dropping `{{estimate.link}}` from an estimate SMS silently removes the call to
 *      action for every tenant, and an uncatalogued token sends as blank text. Neither
 *      stands out inside a paragraph of prose. Renders only when there is something to
 *      report — real additions/removals, an unknown token, or the T10 disclaimer below — not
 *      on every routine review (2026-08-06, at the user's request: this used to always render,
 *      printing "No tokens added or removed" for the common case where nothing needs a
 *      reviewer's attention at all).
 *
 *   2. WORD-LEVEL DIFF, rendered side by side. Removals highlighted in the current pane,
 *      additions highlighted in the proposed pane, computed by LCS over words — not lines.
 *      Line-level would be useless here: template bodies are frequently a single line, and
 *      a line diff would report "the whole body changed" for a one-word edit.
 *
 *   3. FIELD-LEVEL comparison of name, and subject for Email only (2026-08-06: SMS templates
 *      structurally never carry one — chk_template_subject_by_channel — so the row is hidden
 *      rather than always comparing "— to —"), which are separate columns and would otherwise
 *      not appear in a body diff at all. Version is deliberately NOT compared here (also
 *      2026-08-06): comparing it added nothing when the "current" side had no way to answer
 *      anyway — see T10 below, it is "Not available" for nearly every real revision today
 *      regardless. (The version WAS also shown via a header chip at the time this was written;
 *      that chip is gone too now — see the header's own "Comms header chips + cancel" entry.)
 *
 * WHAT IS BLOCKED, AND NOT FAKED:
 *
 *   The "current active version" side cannot be fetched. `MessageTemplateMasterService`
 *   implements `findLive(uid)`, but NO provider-api controller exposes it, and this task
 *   was scoped out of `src/api`. So:
 *
 *     - version 1 (a brand-new template, `isPlainNewTemplate`): there IS no prior active
 *       version — a FACT, not a fetch failure. Originally rendered as a comparison anyway
 *       (every word an addition, honestly labelled "new template, nothing to compare
 *       against"); 2026-08-06, at the user's request, this now renders as plain Name/Subject/
 *       Body values instead — a comparison framing was the wrong metaphor when there is
 *       nothing to compare against at all, not merely an accurate one.
 *
 *     - version > 1 (a revision): the left pane renders `NotAvailableState` naming the
 *       missing endpoint. The proposed content and the FULL token analysis still render,
 *       because both are computable from the queue row alone — and the token analysis is
 *       the part that most needs to be seen. What is NOT shown is any claim about what
 *       changed relative to live, because that claim cannot currently be made.
 *
 *   The diff engine itself is complete and unit-tested; only its left-hand input is
 *   missing. Wiring `GET /api/v1/message-templates/{uid}/live` into `currentVersion` below
 *   is the whole of the remaining work.
 *
 * WHY EVERY COMPARISON CLAIM IS GATED ON `liveUnavailable` (PR 7910, T10):
 *
 *   `currentVersion` is never passed — `Routes.tsx` renders `<ReviewDetail />` with no props
 *   and there is no endpoint to fill it from — so for every revision `resolvedCurrent` is
 *   `undefined` and `diffTemplates` is handed a null left-hand side. The left pane said so
 *   honestly. Everything ABOVE it did not, and each of those was a false statement of fact:
 *
 *     - the token delta listed every token in the proposed body as ADDED and could never
 *       list one as removed. A revision that DELETES `{{estimate.link}}` rendered a panel
 *       titled "Token changes" showing no removal, and — when the proposed body has no
 *       tokens left at all — the words "No tokens added or removed". A reviewer reading
 *       that approves the deletion believing nothing was removed. That is the defect.
 *     - "Body — N words added, 0 removed" asserted a word count that was never computed
 *       against anything.
 *     - the name/subject/version rows highlighted the proposed value as changed against a
 *       current value of "—".
 *     - the proposed pane highlighted every word green, i.e. "all of this is new".
 *
 *   None of that is fixable by fetching harder; there is nothing to fetch. So when
 *   `liveUnavailable` the screen states the token INVENTORY of the proposed body (a fact),
 *   keeps the uncatalogued-token error (computable from the proposed body alone), and says
 *   plainly that removals cannot be detected. Delete these branches when the endpoint lands
 *   — do NOT restore an ungated delta.
 * ==================================================================================
 */

const HIGHLIGHT = {
  add: { backgroundColor: '#e6f4ea', color: '#0b6b2f', borderRadius: '3px', padding: '0 2px' },
  remove: {
    backgroundColor: '#fdecea',
    color: '#8b1a10',
    borderRadius: '3px',
    padding: '0 2px',
    textDecoration: 'line-through',
  },
} as const;

/** One side of the side-by-side. `side` picks which op class is highlighted. */
export const DiffPane: React.FC<{ runs: DiffRun[]; side: 'current' | 'proposed'; testId: string }> = ({
  runs,
  side,
  testId,
}) => {
  // The current pane shows what exists now: equal + removed words. The proposed pane
  // shows what would exist: equal + added words. Each highlights only its own change class.
  const visible = runs.filter((run) =>
    side === 'current' ? run.op !== 'add' : run.op !== 'remove',
  );
  return (
    <Box
      data-testid={testId}
      sx={{
        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
        fontSize: 13,
        lineHeight: 1.7,
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
        minHeight: 120,
      }}
    >
      {visible.length === 0 ? (
        <Typography variant="body2" color="text.disabled">
          (empty)
        </Typography>
      ) : (
        visible.map((run, index) => (
          <Box
            key={`${run.op}-${index}`}
            component="span"
            sx={run.op === 'add' ? HIGHLIGHT.add : run.op === 'remove' ? HIGHLIGHT.remove : undefined}
          >
            {run.value}{' '}
          </Box>
        ))
      )}
    </Box>
  );
};

/** Field-level row for name/subject, which never appear in the body diff. */
const FieldComparison: React.FC<{
  label: string;
  current: string | null;
  proposed: string | null;
  /** True when the live version could not be read at all — see T10 in the header. */
  currentUnavailable?: boolean;
}> = ({ label, current, proposed, currentUnavailable = false }) => {
  // With no live version to read, "changed" is not false — it is UNKNOWN. Highlighting the
  // proposed value as an addition would assert a change nobody has established.
  const changed = !currentUnavailable && (current ?? '') !== (proposed ?? '');
  return (
    <Grid container spacing={2} sx={{ py: 0.75 }}>
      <Grid item xs={3}>
        <Typography variant="caption" color="text.secondary">
          {label}
        </Typography>
      </Grid>
      <Grid item xs={4}>
        <Typography
          variant="body2"
          color={currentUnavailable ? 'text.disabled' : undefined}
          sx={changed && current ? HIGHLIGHT.remove : undefined}
        >
          {currentUnavailable ? 'Not available' : (current ?? '—')}
        </Typography>
      </Grid>
      <Grid item xs={5}>
        <Typography variant="body2" sx={changed ? HIGHLIGHT.add : undefined}>
          {proposed ?? '—'}
        </Typography>
      </Grid>
    </Grid>
  );
};

export interface ReviewDetailProps {
  /** Injected in tests; in the app this arrives via router state from the queue. */
  proposed?: TemplateSummary;
  /**
   * The live version to diff against. `undefined` means "could not be fetched" (no
   * endpoint); `null` means "there genuinely is none" (a version-1 submission). The two
   * are rendered differently on purpose.
   */
  currentVersion?: TemplateSummary | null;
}

export const ReviewDetail: React.FC<ReviewDetailProps> = ({ proposed: injected, currentVersion }) => {
  const { accessToken } = useOidcAccessToken();
  const navigate = useNavigate();
  const location = useLocation();

  const proposed: TemplateSummary | undefined =
    injected ?? (location.state as { row?: TemplateSummary } | null)?.row;

  // A tenant DELETION_REQUEST: approving it RETIRES the template (backend approve() branches to
  // archive), so this screen must not chain a publish the way a normal approval does, and it shows
  // the tenant's reason instead of a content diff. requestKind rides the queue row into router state.
  const isDeletion = proposed?.requestKind === 'DELETION_REQUEST';

  const [notes, setNotes] = useState('');
  const [notesTouched, setNotesTouched] = useState(false);
  const [busy, setBusy] = useState<null | 'approve' | 'reject' | 'changes' | 'publish'>(null);
  const [failure, setFailure] = useState<{ message: string; httpStatus: number | null } | null>(null);
  // Read-only history/thread of this template family, available while deciding — a reviewer should
  // not have to leave the decision screen to see prior versions, diffs, and past review cycles.
  const [showHistory, setShowHistory] = useState(false);
  const [showThread, setShowThread] = useState(false);

  /**
   * The row arrives as a router-state SNAPSHOT taken when the queue was rendered, so its `status`
   * can be arbitrarily old — and approving one submission auto-rejects every other UNDER_REVIEW row
   * for the same `templateMasterUid` (MessageTemplateMasterService#approve, policy decided
   * 2026-07-29). Two reviewers with sibling submissions open is therefore a real way to end up
   * offering Approve/Reject on a row the server has already decided.
   *
   * This re-reads the row's CURRENT status from the manage catalog (the one read that returns every
   * status, not just UNDER_REVIEW) and lets it override the snapshot. It closes the stale-snapshot
   * case deterministically. It does NOT — and cannot — close the concurrent case: any check-then-act
   * loses the race between the read and the click, and only the server is authoritative. So the
   * decision handler re-runs this on a 4xx instead, turning that unavoidable race into the correct
   * "Nothing to decide" terminal state rather than a red error banner.
   */
  const [serverStatus, setServerStatus] = useState<TemplateStatus | null>(null);
  // The WHOLE reconciled row, not just its status: a row decided by another reviewer carries the
  // reviewer's reason in `reviewNotes`, which the router-state snapshot (usually still null in that
  // race) does not have. The "Reason given" box below prefers this over the snapshot so the outcome
  // is shown WITH its explanation, which is the case the reconcile exists for (2026-08-11).
  const [serverRow, setServerRow] = useState<TemplateSummary | null>(null);

  const reconcileStatus = useCallback(async () => {
    if (!proposed || !accessToken) {
      return;
    }
    const result = await fetchManageCatalog(accessToken);
    if (result.kind !== 'ok') {
      return; // Leave the snapshot in place; the server still refuses a stale decision.
    }
    const row = result.data.find(
      (t) => t.templateMasterUid === proposed.templateMasterUid && t.version === proposed.version,
    );
    if (row) {
      setServerStatus(row.status);
      setServerRow(row);
    }
  }, [accessToken, proposed]);

  useEffect(() => {
    void reconcileStatus();
  }, [reconcileStatus]);

  const notesRef = useRef<HTMLTextAreaElement | null>(null);

  /**
   * Inserts at the caret, or appends when the field has never been focused — identical logic
   * to TemplateEditor's own `insertToken`, retargeted at the review comments instead of a
   * template body. 2026-08-06, at the user's request: a reviewer rejecting or requesting
   * changes often wants to tell the tenant author WHICH token to use instead of a broken one
   * (see the unknown-token warning above) — typing `{{client.firstName}}` from memory is easy
   * to get wrong; picking it is not.
   */
  const insertNoteToken = useCallback(
    (placeholder: string) => {
      const el = notesRef.current;
      if (!el || el.selectionStart == null) {
        setNotes((prev) => (prev === '' ? placeholder : `${prev}${placeholder}`));
        return;
      }
      const start = el.selectionStart;
      const end = el.selectionEnd ?? start;
      setNotes((prev) => `${prev.slice(0, start)}${placeholder}${prev.slice(end)}`);
      const restoreCaret = () => {
        el.focus();
        const caret = start + placeholder.length;
        el.setSelectionRange(caret, caret);
      };
      if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
        window.requestAnimationFrame(restoreCaret);
      }
    },
    [],
  );

  const isNewTemplate = proposed != null && requestTypeOf(proposed.version) === 'NEW_TEMPLATE';
  // A version-1 submission has no prior live version — that is a fact, not a fetch failure.
  const resolvedCurrent: TemplateSummary | null | undefined = isNewTemplate ? null : currentVersion;
  const liveUnavailable = resolvedCurrent === undefined;
  /**
   * True exactly for the case where there is DEFINITELY nothing to compare against (a v1
   * submission) — not "could not be fetched" (`liveUnavailable`). 2026-08-06, at the user's
   * request: a "current vs proposed" comparison table/diff is the wrong metaphor entirely
   * when there is no "current" — Name/Subject/Body below render as plain values instead,
   * the same way the authoring screen shows them, not paired against a column of "—".
   */
  const isPlainNewTemplate = resolvedCurrent === null;

  /**
   * 2026-08-11: when the live version cannot be loaded (there is no provider-api read for it yet), a
   * "current active version vs proposed" comparison is a frame with no left-hand side, and the
   * disclaimers that explained the missing endpoint (method names, routes, "not routed") were
   * dev-facing noise on a reviewer's screen. So a revision with no loadable live version now renders
   * the PROPOSED content plainly — exactly like a v1 submission — instead of an empty comparison.
   * Only a genuinely loadable live version (when that endpoint lands) produces the side-by-side diff.
   */
  const showProposedPlainly = isPlainNewTemplate || liveUnavailable;

  const diff = useMemo(
    () => diffTemplates(resolvedCurrent?.body ?? null, proposed?.body ?? null),
    [resolvedCurrent, proposed],
  );

  if (!proposed) {
    // Reached on a hard refresh / deep link: router state is gone and there is no
    // GET /{uid} to recover it from. Rather than show a dead-end page, send the reviewer
    // straight back to the Template Review queue (user request 2026-08-07) — that tab is
    // where a review is meant to be opened from, and it always has the content.
    return <Navigate to="/communications/templates" state={{ tab: 'review' }} replace />;
  }

  // Structural, not a preference: SMS templates never carry a subject (chk_template_subject_by_channel
  // rejects one), so comparing "Subject: — / —" on every SMS review is not a missing value, it is a
  // row that can never mean anything for this channel. See TemplateEditor's identical `isEmail` gate.
  const isEmail = proposed.channel === CHANNEL_EMAIL;

  /**
   * 2026-08-06 fix — the actual bug behind "Reject is not working correctly". Every row on the
   * Email/SMS Templates tabs (any manage-catalog status, not just UNDER_REVIEW — see
   * `MessageTemplateMasterRepository#findManageCatalog`) opens Review Detail on click, but
   * `MessageTemplateMasterService` enforces UNDER_REVIEW-only for all three decisions —
   * `reject`/`requestChanges` share `lockAndRequireReviewable`, `approve` has an equivalent
   * inline check — so a CHANGES_REQUESTED, DRAFT, APPROVED, or PUBLISHED row 400s on every one
   * of them ("Only an UNDER_REVIEW version may be reviewed/approved; ..."). The UI never
   * checked this itself, so clicking Reject on anything else looked broken: the button appeared
   * to do nothing useful, surfacing only an opaque backend 400 in the generic error banner.
   * Gates the whole Decision section (and the blast-radius warning above, which also describes
   * an action that would not actually be available) on the row's real status instead of always
   * assuming one is pending.
   */
  // `serverStatus` is the re-read above; it wins over the router-state snapshot whenever it has
  // landed, so a row another reviewer already decided gates to "Nothing to decide" like any other
  // non-pending row instead of offering buttons that can only 400.
  const effectiveStatus = serverStatus ?? proposed.status;
  const isUnderReview = effectiveStatus === 'UNDER_REVIEW';
  // An APPROVED submission was signed off but never published — the review screen offers Publish
  // (the one remaining action) rather than a "Nothing to decide" dead end (2026-08-11).
  const canPublishApproved = effectiveStatus === 'APPROVED';
  // Prefer the reconciled row's reason over the router-state snapshot's: in the race the reconcile
  // exists for (another reviewer decided this row), the snapshot's `reviewNotes` is stale/null while
  // the freshly-read row carries the real reason.
  const reviewReason = serverRow?.reviewNotes ?? proposed.reviewNotes;

  const notesMissing = notes.trim() === '';

  const describeFailure = (result: {
    kind: string;
    status?: number | null;
    message?: string;
  }): { message: string; httpStatus: number | null } => {
    if (result.kind === 'forbidden') {
      return { message: 'Refused — this action needs provider-admin permission.', httpStatus: 403 };
    }
    if (result.kind === 'unauthorized') {
      return { message: 'Your session is not valid. Sign in again.', httpStatus: 401 };
    }
    if (result.kind === 'notFound') {
      return { message: `${result.message} — it may have been decided by another reviewer.`, httpStatus: 404 };
    }
    return { message: result.message ?? 'The request failed', httpStatus: result.status ?? null };
  };

  const runDecision = async (decision: 'approve' | 'reject' | 'changes') => {
    if (decision !== 'approve' && notesMissing) {
      setNotesTouched(true);
      return;
    }
    setBusy(decision);
    setFailure(null);

    /**
     * APPROVE NOW PUBLISHES IN THE SAME CLICK (2026-08-11, at the user's request).
     *
     * Since PR-20 / ADR-004 (V1_5_0_0_89) "live" means PUBLISHED, not APPROVED — approve() and
     * publish() are decoupled, so approving alone left the submission in an intermediate APPROVED
     * state that is NOT live for tenants, requiring a second Publish click on the list. That
     * two-step limbo is exactly the "what is this Approved state" confusion this collapses: approve
     * then immediately publish, so one click makes a tenant submission live for every tenant.
     *
     * publish() accepts an APPROVED version (see `publishTemplate`), so this is a pure two-call
     * frontend chain — no backend change. If the publish half fails, the version is left APPROVED
     * and the list's own Publish action is the recovery path, which the error message names.
     */
    if (decision === 'approve') {
      // Land-on-live navigation, shared by the "just published" and "already live" outcomes.
      const goLive = (already: boolean) =>
        navigate('/communications/templates', {
          state: {
            tab: 'review',
            savedNotice: {
              title: 'Published',
              message: already
                ? 'This version is already live for every tenant.'
                : 'This version is now live for every tenant.',
            },
          },
          replace: true,
        });

      // A DELETION_REQUEST approval is terminal in one call: the backend approve() retires the whole
      // family instead of promoting the copy, so there is NOTHING to publish afterwards. Call approve
      // once and land on "Removed"; a 409 here is the backend's "still referenced by a plan step"
      // guard, shown verbatim so the reviewer knows why the removal was refused.
      if (isDeletion) {
        const removed = await approveTemplate(proposed.templateMasterUid, proposed.version, accessToken);
        setBusy(null);
        if (removed.kind === 'ok') {
          navigate('/communications/templates', {
            state: {
              tab: 'review',
              savedNotice: {
                title: 'Removed',
                message: `"${proposed.name}" was removed — it is no longer available to any tenant.`,
              },
            },
            replace: true,
          });
          return;
        }
        setFailure(describeFailure(removed));
        if (removed.kind === 'notFound' || removed.kind === 'rejected') {
          void reconcileStatus();
        }
        return;
      }

      // Step 1 — record the human review decision (UNDER_REVIEW -> APPROVED). A genuine refusal
      // (another submission pending, not-under-review, gone, no authority) is shown verbatim, as
      // before. The ONE exception is the stale/re-opened-tab case, recognised by the server's own
      // words: if the version is ALREADY PUBLISHED it is already live (success); if it is ALREADY
      // APPROVED the human decision is already recorded, so we skip straight to publishing it.
      //
      // This is deliberately keyed on the message and NOT on an error code (review thread #66534):
      // approve() throws a bare BadRequestException with no code, and publish() reuses one code
      // (TEMPLATE_PUBLISH_NOT_APPROVED) across BOTH the idempotent "already PUBLISHED" success and
      // the genuine "still DRAFT" failure — so the code cannot tell success from failure; only the
      // "... is <STATUS>" suffix can. A code-based version needs new status-specific codes on both
      // endpoints plus re-baselining their tests (out of scope for this UX PR). The match is safe in
      // the failure direction: if the wording ever changes it simply misses and a visible error is
      // shown (the list's Publish action stays the recovery path) — never a false "published".
      const approved = await approveTemplate(proposed.templateMasterUid, proposed.version, accessToken);
      if (approved.kind !== 'ok') {
        const approveMessage = (approved as { message?: string }).message ?? '';
        if (/is PUBLISHED\b/.test(approveMessage)) {
          setBusy(null);
          goLive(true);
          return;
        }
        if (!/is APPROVED\b/.test(approveMessage)) {
          setBusy(null);
          setFailure(describeFailure(approved));
          // Same race reconcile as reject/changes (crm_phase2): a 404 or business refusal may mean
          // another reviewer decided this row first — re-read so it settles on "Nothing to decide"
          // rather than leaving the buttons enabled above a red banner.
          if (approved.kind === 'notFound' || approved.kind === 'rejected') {
            void reconcileStatus();
          }
          return;
        }
        // else: already APPROVED — fall through to publish it.
      }

      // If approve already made it live, there is nothing left to publish. The "approve as
      // submitted" backend path (a clean approval with no reviewer edits) approves AND publishes
      // in the one call, returning a PUBLISHED summary. Firing publish again would 409
      // ("... is PUBLISHED"); the Step-2 guard below still treats that as success, but the
      // redundant round-trip logs a browser console error, so skip it when we can already see
      // the row is live. (A stale-tab race where the status arrives as something else still
      // falls through to Step 2, whose message-based 409 handling remains the safety net.)
      if (approved.kind === 'ok' && approved.data.status === 'PUBLISHED') {
        setBusy(null);
        goLive(true);
        return;
      }

      // Step 2 — publish (APPROVED -> PUBLISHED, live for every tenant). A publish 409 whose message
      // says the version is already PUBLISHED is the same stale-click case and counts as success.
      const published = await publishTemplate(proposed.templateMasterUid, proposed.version, accessToken);
      setBusy(null);
      const publishMessage = (published as { message?: string }).message ?? '';
      if (published.kind === 'ok' || /is PUBLISHED\b/.test(publishMessage)) {
        goLive(published.kind !== 'ok');
        return;
      }
      const detail = describeFailure(published);
      setFailure({
        message:
          `Approved, but publishing did not complete: ${detail.message} This version is Approved `
          + 'but not yet live — use its Publish action on the template list to publish it.',
        httpStatus: detail.httpStatus,
      });
      // The server row is now APPROVED even though publish failed. Reconcile so the screen flips
      // into the canPublishApproved branch (a Publish recovery button) instead of leaving the
      // UNDER_REVIEW decision buttons live above the banner — every one of which can now only 400.
      void reconcileStatus();
      return;
    }

    const result =
      decision === 'reject'
        ? await rejectTemplate(proposed.templateMasterUid, proposed.version, notes.trim(), accessToken)
        : await requestTemplateChanges(
            proposed.templateMasterUid,
            proposed.version,
            notes.trim(),
            accessToken,
          );
    setBusy(null);

    if (result.kind === 'ok') {
      // No standalone "Decision recorded" dead-end page — a reviewer who just decided wants the
      // NEXT item, not a screen whose only action is "back". Land straight on the review queue and
      // carry the outcome as the list's own success banner (the same savedNotice channel the editor
      // save-flow already uses). `replace` so Back doesn't return to a now-decided review.
      const message =
        decision === 'reject'
          ? 'Rejected. This version is terminal and cannot be revived.'
          : 'Sent back to the tenant for rework.';
      navigate('/communications/templates', {
        state: { tab: 'review', savedNotice: { title: 'Decision recorded', message } },
        replace: true,
      });
      return;
    }
    setFailure(describeFailure(result));
    // crm_phase2 fix(comms): the row may have moved under us (another reviewer decided it). On a
    // 404, or a 400/409 business refusal (the server's UNDER_REVIEW-only guard firing), re-read the
    // row status so a now-decided submission stops offering Approve/Reject — `isUnderReview` flips
    // and the Decision section is replaced by the existing "Nothing to decide" notice — rather than
    // leaving enabled buttons above a red banner. Any other failure leaves the status as-is.
    if (result.kind === 'notFound' || result.kind === 'rejected') {
      void reconcileStatus();
    }
  };

  /**
   * Publish an APPROVED submission straight from the review screen (2026-08-11, at the user's
   * request): a reviewer signed it off earlier but it never went live, so the one action left is to
   * publish it. No approve step — it is already APPROVED — just publishTemplate (APPROVED ->
   * PUBLISHED). An "already PUBLISHED" 409 means someone beat us to it and counts as success.
   */
  const publishApproved = async () => {
    setBusy('publish');
    setFailure(null);
    const published = await publishTemplate(proposed.templateMasterUid, proposed.version, accessToken);
    setBusy(null);
    const publishMessage = (published as { message?: string }).message ?? '';
    if (published.kind === 'ok' || /is PUBLISHED\b/.test(publishMessage)) {
      navigate('/communications/templates', {
        state: {
          tab: 'review',
          savedNotice: {
            title: 'Published',
            message: published.kind !== 'ok'
              ? 'This version is already live for every tenant.'
              : 'This version is now live for every tenant.',
          },
        },
        replace: true,
      });
      return;
    }
    setFailure(describeFailure(published));
    if (published.kind === 'notFound' || published.kind === 'rejected') {
      void reconcileStatus();
    }
  };

  const tokens = diff.tokens;
  /**
   * True when there is a real token-level difference worth a reviewer's attention: something
   * added, something removed, or an uncatalogued token in the proposed body. See the token-delta
   * panels' own comment below for why this is what they gate on.
   */
  const hasTokenChange =
    tokens.added.length > 0 || tokens.removed.length > 0 || tokens.unknownInProposed.length > 0;
  // Highlighting the proposed pane green means "these words are new". Against a left-hand side
  // that was never loaded, nothing is known to be new, so the body renders as plain text.
  const proposedRuns: DiffRun[] = liveUnavailable
    ? [{ op: 'equal', value: proposed.body ?? '' }]
    : diff.runs;

  return (
    <Box sx={{ p: 3 }} data-testid="review-detail">
      {/* 2026-08-11: the "Approving publishes to every tenant at once" blast-radius banner was
          removed at the user's request — it was standing noise on every revision review. */}

      {/* A DELETION_REQUEST is a tenant asking to REMOVE this template. The reviewer's decision is not
          "publish this content" but "remove it, or decline" — so the reason is surfaced up top, and
          "Approve removal" retires the whole family (the body below is an unchanged copy, shown only
          because the request rides the normal content-review plumbing). */}
      {isDeletion && (
        <Alert severity="warning" sx={{ mb: 2 }} data-testid="deletion-request-banner">
          <AlertTitle>Removal requested by the tenant</AlertTitle>
          Approving this removes &quot;{proposed.name}&quot; from every tenant (a soft delete — kept in
          history, restorable by support). Reason given:{' '}
          <strong data-testid="deletion-request-reason">{proposed.requestReason || '—'}</strong>
        </Alert>
      )}

      {/*
        ---- Layer 1: token delta, above the prose, because it matters most ----
        2026-08-06: this panel used to always render, printing "No tokens added or removed"
        for the overwhelmingly common case where nothing token-related needs a reviewer's
        attention — noise on every routine review, at the user's request to remove it. Gated
        on `liveUnavailable || hasTokenChange` instead: `hasTokenChange` already covers real
        additions, removals, AND unknown tokens (see its definition above), so the panel now
        renders exactly when there is something to say and stays silent otherwise. `liveUnavailable`
        keeps its own unconditional OR — the T10 disclaimer above is owed to the reviewer even
        when the proposed body happens to carry no tokens at all, since ITS point is "no claim
        can be made", not "nothing changed".
      */}

      {/* 2026-08-13 correction (PR 8063 review): `hasTokenChange` already includes
          `tokens.unknownInProposed.length > 0` as one of its ORs (see its own definition above), so
          `(liveUnavailable || hasTokenChange)` is always true whenever the left-hand condition here
          is true — the extra clause looked like a second gate but never actually affected the
          outcome. Simplified to what it always reduced to. */}
      {tokens.unknownInProposed.length > 0 && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }} data-testid="token-delta">
          <Alert severity="error" data-testid="unknown-token-warning">
            <AlertTitle>This body uses token(s) that do not exist</AlertTitle>
            {tokens.unknownInProposed.map((t) => `{{${t}}}`).join(', ')} — nothing populates these at
            send time, so they will render as blank text for every tenant. Approving this ships a
            broken message.
          </Alert>
        </Paper>
      )}
      {!liveUnavailable && (tokens.added.length > 0 || tokens.removed.length > 0) && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }} data-testid="token-delta-changes">
          <Typography variant="subtitle2" gutterBottom>
            Token changes
          </Typography>
          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
            {tokens.removed.map((t) => (
              <Chip key={`r-${t}`} size="small" color="error" variant="outlined" label={`− {{${t}}}`} />
            ))}
            {tokens.added.map((t) => (
              <Chip key={`a-${t}`} size="small" color="success" variant="outlined" label={`+ {{${t}}}`} />
            ))}
          </Stack>
        </Paper>
      )}

      {/*
        ---- Layers 2+3, for a v1 submission: read-only fields, not a comparison ----
        2026-08-06, at the user's request — a screenshot of the "New template" authoring screen
        (`TemplateEditor.tsx`) as the target look: a v1 submission (`isPlainNewTemplate`) shows
        Name/Subject/Body as read-only fields styled like that screen, in ONE Paper — no "Current
        active version | Proposed" columns, no "N words added" summary, nothing paired against a
        "—". `InputProps={{ readOnly: true }}` rather than `disabled`: disabled greys the text out,
        and a reviewer needs to read this clearly, not be told it is unavailable. A revision (real
        diff, or T10's liveUnavailable) keeps the comparison table AND the side-by-side diff below
        exactly as before: there, "current" is a real, meaningful question, just not always an
        answerable one yet.
      */}
      {showProposedPlainly ? (
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Stack spacing={2}>
            <TextField
              fullWidth
              size="small"
              label="Template name"
              value={proposed.name}
              InputProps={{ readOnly: true }}
              inputProps={{ 'data-testid': 'plain-name' }}
            />
            {isEmail && (
              <TextField
                fullWidth
                size="small"
                label="Subject"
                value={proposed.subject ?? ''}
                InputProps={{ readOnly: true }}
                inputProps={{ 'data-testid': 'plain-subject' }}
              />
            )}
            {isEmail ? (
              <Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block', mb: 0.5 }}
                >
                  Body preview
                </Typography>
                <HtmlPreview
                  html={proposed.body ?? ''}
                  testId="plain-body-preview"
                  minHeight={320}
                />
              </Box>
            ) : (
              <TextField
                fullWidth
                multiline
                minRows={6}
                label="Body"
                value={proposed.body ?? ''}
                InputProps={{ readOnly: true }}
                inputProps={{ 'data-testid': 'plain-body' }}
              />
            )}
          </Stack>
        </Paper>
      ) : (
        <>
          <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={3} />
              <Grid item xs={4}>
                <Typography variant="overline" color="text.secondary">
                  Current active version
                </Typography>
              </Grid>
              <Grid item xs={5}>
                <Typography variant="overline" color="primary">
                  Proposed
                </Typography>
              </Grid>
            </Grid>
            <Divider sx={{ mb: 1 }} />
            <FieldComparison
              label="Name"
              current={resolvedCurrent?.name ?? null}
              proposed={proposed.name}
              currentUnavailable={liveUnavailable}
            />
            {/*
              Email-only — SMS templates never carry a subject (chk_template_subject_by_channel),
              so this row was "Subject: — / —" on every SMS review, never once meaning anything.
              The Version row that used to follow this one is gone entirely: its "current" side
              was "Not available" for nearly every real revision today anyway (T10 —
              liveUnavailable is true until the missing GET /{uid}/live endpoint lands), so it
              added a comparison with nothing to compare. (It used to also be redundant with a
              version chip in the header — that chip is gone now too, removed separately.)
            */}
            {isEmail && (
              <FieldComparison
                label="Subject"
                current={resolvedCurrent?.subject ?? null}
                proposed={proposed.subject}
                currentUnavailable={liveUnavailable}
              />
            )}
          </Paper>

          <Typography variant="subtitle2" sx={{ mb: 1 }} data-testid="body-summary">
            Body —{' '}
            {liveUnavailable
              ? 'the proposed body in isolation. The live version could not be loaded, so no word or token count is a comparison.'
              : summariseDiff(diff)}
          </Typography>
          {diff.degraded && (
            <Alert severity="warning" sx={{ mb: 1 }}>
              These bodies are too large for a word-level comparison; the panes below show a
              whole-body replace rather than a precise diff.
            </Alert>
          )}

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                <Typography variant="overline" color="text.secondary">
                  Current active version
                </Typography>
                <Divider sx={{ my: 1 }} />
                {/* This whole comparison branch renders only when a live version IS loadable, so the
                    left pane is always the real diff. The old NotAvailableState (which named the
                    missing endpoint / backend method) was dev-facing and is gone — an unloadable
                    live version now shows the proposed content plainly, above. */}
                <DiffPane runs={diff.runs} side="current" testId="diff-pane-current" />
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                <Typography variant="overline" color="primary">
                  Proposed
                </Typography>
                <Divider sx={{ my: 1 }} />
                <DiffPane runs={proposedRuns} side="proposed" testId="diff-pane-proposed" />
              </Paper>
            </Grid>
          </Grid>

          {isEmail && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="overline" color="primary" sx={{ display: 'block', mb: 0.5 }}>
                Proposed — rendered preview
              </Typography>
              <HtmlPreview
                html={proposed.body ?? ''}
                testId="proposed-body-preview"
                minHeight={320}
              />
            </Box>
          )}
        </>
      )}

      {/* 2026-08-11: the "Last reviewed <date> by <reviewer>" line was removed entirely at the
          user's request — the status and (for a decided row) the reason are what matter here. */}

      {/*
        ---- Decision ----
        2026-08-06, at the user's request: a compact "Insert a token" dropdown in the header row,
        next to "Decision" — replaces the earlier full `TokenPicker` panel/column, which was too
        much permanent screen space for an occasional insert on this screen (unlike
        TemplateEditor's authoring screen, where a persistent picker is the point). Stateless by
        design: `value` is hard-coded to `''` always, so the control never shows a token as
        "selected" — picking one fires `insertNoteToken` as a one-shot action and the dropdown
        resets to its placeholder, the same way a `TokenPicker` chip click never stays "pressed".

        2026-08-06 fix, same day: the dropdown, the notes field, and the three decision buttons
        are now all gated on `isUnderReview` — see that constant's own doc comment for the bug
        this closes. Cancel is the one thing that stays either way: whatever the status, leaving
        the screen must always be possible.
      */}
      <Paper variant="outlined" sx={{ p: 2, mt: 3 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
          <Typography variant="subtitle2">Decision</Typography>
          {isUnderReview && (
            <FormControl size="small" sx={{ minWidth: 200 }} disabled={busy !== null}>
              <InputLabel id="notes-token-label">Insert a token</InputLabel>
              <Select
                labelId="notes-token-label"
                label="Insert a token"
                value=""
                onChange={(event: SelectChangeEvent) => insertNoteToken(placeholderFor(event.target.value))}
                inputProps={{ 'data-testid': 'notes-token-select' }}
              >
                {tokensByGroup().flatMap((group) => [
                  <ListSubheader key={group.group}>{group.label}</ListSubheader>,
                  ...group.tokens.map((token) => (
                    <MenuItem
                      key={token.tokenKey}
                      value={token.tokenKey}
                      data-testid={`notes-token-option-${token.tokenKey}`}
                    >
                      {token.label}
                    </MenuItem>
                  )),
                ])}
              </Select>
            </FormControl>
          )}
        </Stack>

        {isUnderReview ? (
          <>
            {failure && (
              // A decision ACTION failure, not a load failure — a plain error Alert, the same shape
              // the canPublishApproved branch uses. ErrorState's "Could not load — this is not an
              // empty list" title made no sense for an approve/reject that 400'd (2026-08-11).
              <Alert severity="error" sx={{ mb: 2 }} data-testid="state-error">
                {failure.message}
                {failure.httpStatus != null && ` (HTTP ${failure.httpStatus})`}
              </Alert>
            )}
            <TextField
              fullWidth
              multiline
              minRows={3}
              label="Review comments"
              placeholder="Required to reject or request changes — the tenant sees this."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              onBlur={() => setNotesTouched(true)}
              error={notesTouched && notesMissing}
              inputRef={notesRef}
              inputProps={{ 'data-testid': 'review-notes' }}
              sx={{ mb: 1 }}
            />
            {notesTouched && notesMissing && (
              <FormHelperText error data-testid="notes-required">
                Comments are required to reject or request changes.
              </FormHelperText>
            )}
            {proposed && (
              <Stack direction="row" spacing={1} sx={{ mb: 1.5 }}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setShowHistory(true)}
                  data-testid="review-detail-history-button"
                >
                  Version history
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setShowThread(true)}
                  data-testid="review-detail-thread-button"
                >
                  Review thread
                </Button>
              </Stack>
            )}
            {proposed && showHistory && (
              <TemplateVersionHistory template={proposed} onClose={() => setShowHistory(false)} />
            )}
            {proposed && showThread && (
              <TemplateReviewThread template={proposed} onClose={() => setShowThread(false)} />
            )}
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 1.5 }}>
              <Stack direction="row" spacing={1}>
                <Button
                  variant="contained"
                  color={isDeletion ? 'error' : 'success'}
                  disabled={busy !== null}
                  onClick={() => runDecision('approve')}
                  data-testid="approve-button"
                >
                  {isDeletion
                    ? (busy === 'approve' ? 'Removing…' : 'Approve removal')
                    : (busy === 'approve' ? 'Publishing…' : 'Publish')}
                </Button>
                <Button
                  variant="outlined"
                  color="warning"
                  disabled={busy !== null}
                  onClick={() => runDecision('changes')}
                  data-testid="request-changes-button"
                >
                  Request changes
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  disabled={busy !== null}
                  onClick={() => runDecision('reject')}
                  data-testid="reject-button"
                >
                  Reject
                </Button>
              </Stack>
              {/*
                2026-08-06, at the user's request: a way out of this screen that is not a
                decision — "Back to queue" above already does the SAME navigation, but that
                button sits far from where a reviewer's eye lands after reading the Decision
                buttons, having just decided NOT to decide. Same target TemplateEditor's own
                Cancel button uses (`/communications/templates`, defaults to the Email tab) —
                deliberately not `/communications/review` (the standalone queue page "Back to
                queue" targets): this is "give up and go look at templates", not "go back to
                reviewing".
              */}
              <Button
                variant="text"
                disabled={busy !== null}
                onClick={() => navigate('/communications/templates')}
                data-testid="cancel-button"
              >
                Cancel
              </Button>
            </Stack>
          </>
        ) : canPublishApproved ? (
          <>
            {failure && (
              <Alert severity="error" sx={{ mb: 2 }} data-testid="state-error">
                {failure.message}
                {failure.httpStatus != null && ` (HTTP ${failure.httpStatus})`}
              </Alert>
            )}
            {/* APPROVED: this version passed review but is not live. Publish is the one valid action
                (Reject / Request changes are UNDER_REVIEW-only on the backend, so they are not
                offered here). */}
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Button
                variant="contained"
                color="success"
                disabled={busy !== null}
                onClick={publishApproved}
                data-testid="publish-approved-button"
              >
                {busy === 'publish' ? 'Publishing…' : 'Publish'}
              </Button>
              <Button
                variant="text"
                disabled={busy !== null}
                onClick={() => navigate('/communications/templates')}
                data-testid="cancel-button"
              >
                Cancel
              </Button>
            </Stack>
          </>
        ) : (
          <>
            <Alert severity="info" sx={{ mb: 2 }} data-testid="not-under-review-notice">
              <AlertTitle>Nothing to decide — this submission is {statusLabel(effectiveStatus)}</AlertTitle>
              {effectiveStatus === 'CHANGES_REQUESTED'
                ? 'A reviewer already sent this back for rework. It returns here, reviewable '
                  + 'again, only once the tenant resubmits it.'
                : effectiveStatus === 'REJECTED'
                  ? 'This submission was rejected and will not go live.'
                  : 'Only a submission that is Under review can be approved, sent back for '
                    + 'changes, or rejected.'}
              {/* 2026-08-11: show the reviewer's REASON (the review notes) for a decided submission,
                  so the "why" is kept on screen rather than only the status. Shown only when notes
                  exist. */}
              {(effectiveStatus === 'CHANGES_REQUESTED' || effectiveStatus === 'REJECTED')
                && reviewReason
                && reviewReason.trim() !== '' && (
                  <Box sx={{ mt: 1 }} data-testid="review-reason">
                    <strong>Reason given:</strong> {reviewReason}
                  </Box>
                )}
            </Alert>
            <Button
              variant="text"
              onClick={() => navigate('/communications/templates')}
              data-testid="cancel-button"
            >
              Cancel
            </Button>
          </>
        )}
      </Paper>
    </Box>
  );
};

export default ReviewDetail;
